"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFeatureAssignmentValue = exports.evaluationCriteriaToKey = exports.targetingPropertiesToKey = exports.objectToString = void 0;
const lodash_1 = __importDefault(require("lodash"));
function objectToString(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
obj) {
    if (lodash_1.default.isNil(obj)) {
        return 'null';
    }
    const sortedKeys = lodash_1.default.sortBy(lodash_1.default.keys(obj));
    const keyValuePairs = sortedKeys.map((key) => {
        const value = obj[key];
        if (lodash_1.default.isObject(value) && !lodash_1.default.isArray(value)) {
            // Recursively handle nested objects (excluding arrays)
            return `${key}=${objectToString(value)}`;
        }
        else {
            // Handle null values and convert other values to string
            const strObject = value || '';
            return `${key}=${strObject}`;
        }
    });
    return keyValuePairs.join('|');
}
exports.objectToString = objectToString;
/**
 * Generates a string representation of targeting properties to be used
 * as a map key.
 *
 * @param targetingProperties Optional. object that will be converted to it's custom
 * string representation.
 * @returns string represenrarion.
 */
function targetingPropertiesToKey(targetingProperties) {
    return objectToString(targetingProperties);
}
exports.targetingPropertiesToKey = targetingPropertiesToKey;
/**
 * Converts an EvaluationCriteria object into a string key.
 *
 * This function takes an object of type EvaluationCriteria, extracts its featureKey,
 * subjectKey, and targetingProperties, and then constructs a string key by concatenating
 * these elements. The targetingProperties are converted into a key string using
 * the targetingPropertiesToKey function. The resulting key string is a comma-separated
 * value containing the featureKey, subjectKey, and the targeting properties key.
 *
 * @param {EvaluationCriteria} evaluationCriteria - An object of type EvaluationCriteria
 *      which includes the featureKey, subjectKey, and targetingProperties.
 * @returns {string} A string key representing the combination of featureKey, subjectKey,
 *      and the targeting properties key.
 */
function evaluationCriteriaToKey(evaluationCriteria) {
    const propertiesKey = targetingPropertiesToKey(evaluationCriteria.targetingProperties);
    return `${evaluationCriteria.featureKey},${evaluationCriteria.subjectKey},${propertiesKey}`;
}
exports.evaluationCriteriaToKey = evaluationCriteriaToKey;
/**
 * Determines and returns the value of a given feature assignment.
 *
 * This function evaluates the provided `FeatureAssignmentValue` object and returns its value based on a
 * predefined order of precedence among its properties. The function handles different types of values including
 * boolean, numeric, and string. If none of these values are set, the function defaults to returning the `defaultValue`
 * property, if present. In cases where a feature has an `errorValue`, indicating an error state, the function
 * returns `null` to signify the unavailability of a valid value.
 *
 * The order of precedence for returning values is as follows:
 * 1. booleanValue
 * 2. numericValue
 * 3. stringValue
 * 4. defaultValue
 * If none of these properties are set or if `errorValue` is defined, the function returns `null`.
 * @param {FeatureAssignmentValue} feature - The feature assignment object containing potential value properties.
 * @returns {boolean | number | string | null} The value of the feature assignment based on the available properties,
 * or `null` if no valid value is found or if an error state is indicated.
 */
function getFeatureAssignmentValue(feature) {
    // Handle the special case for errorValue
    if (feature.errorValue !== undefined) {
        return null;
    }
    if (feature.booleanValue !== undefined) {
        return feature.booleanValue;
    }
    if (feature.numericValue !== undefined) {
        return feature.numericValue;
    }
    if (feature.stringValue !== undefined) {
        return feature.stringValue;
    }
    if (feature.defaultValue !== undefined) {
        return feature.defaultValue;
    }
    return null;
}
exports.getFeatureAssignmentValue = getFeatureAssignmentValue;
